import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { mapStyleConfig } from "keplerConfigs/config/mapStyleConfig";
import { mapStateConfig } from "keplerConfigs/config/mapStateConfig";
import { Box, Grid } from "@mui/material";
import _ from "lodash";
import { Loading } from "components/common";
import Request from "core/requests";
import { useDispatch, useSelector } from "react-redux";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import Co2Icon from "@mui/icons-material/Co2";
import { IconWind, IconUsers, IconCloudRain, IconDroplet } from "@tabler/icons";
import { getCo2Heatmap } from "keplerConfigs/config/homeMap/dataset/metrics/co2";
import { getUmidityHeatmap } from "keplerConfigs/config/homeMap/dataset/metrics/umidity";
import { getTempHeatmap } from "keplerConfigs/config/homeMap/dataset/metrics/airTemp";
import { getLuxHeatmap } from "keplerConfigs/config/homeMap/dataset/metrics/lux";
import { getBarPressHeatmap } from "keplerConfigs/config/homeMap/dataset/metrics/barPress";
import {
  getPM2_5Heatmap,
  getPM10Heatmap,
} from "keplerConfigs/config/homeMap/dataset/metrics/pm";
import { getPCountHeatmap } from "keplerConfigs/config/homeMap/dataset/metrics/pcount";
import { getWindSpeedAvgHeatmap } from "keplerConfigs/config/homeMap/dataset/metrics/wind";
import { getRainIntensityHeatmap } from "keplerConfigs/config/homeMap/dataset/metrics/rain";
import {
  getHeatmapCO2Norm,
  getHeatmapPM10Norm,
  getHeatmapPM2_5Norm,
} from "keplerConfigs/config/environmentMapConfig/heatmapsConfig";
import {
  getPointCO2Heatmap,
  getPointPM10Heatmap,
  getPointPM2_5Heatmap,
} from "keplerConfigs/config/environmentMapConfig/pointsConfig";
import SpeedIcon from "@mui/icons-material/Speed";
import { WiDust } from "react-icons/wi";
import { DateTime } from "luxon";
import { saveCluster } from "store/user.store";
import { logout, refreshToken } from "store/user.store";
import { openSnackbar } from "store/slices/snackbar";
import { useTranslation } from "react-i18next";
import FiltersKeplerMapContainer from "containers/kepler-maps/FiltersKeplerMap.container";
import { getPointConfigRainfallHeatmap } from "keplerConfigs/config/environmentMapConfig/pointsConf/rain";
import { getPointConfigWindSpeedHeatmap } from "keplerConfigs/config/environmentMapConfig/pointsConf/wind";
import { getPointConfigPCountHeatmap } from "keplerConfigs/config/environmentMapConfig/pointsConf/pcount";
import { getPointConfigBarPressHeatmap } from "keplerConfigs/config/environmentMapConfig/pointsConf/barPress";
import { getPointConfigUmidityHeatmap } from "keplerConfigs/config/environmentMapConfig/pointsConf/umidity";
import { getPointConfigTempHeatmap } from "keplerConfigs/config/environmentMapConfig/pointsConf/airTemp";
import { getPointConfigLuxHeatmap } from "keplerConfigs/config/environmentMapConfig/pointsConf/lux";
import { getHeatmapRainfallNorm } from "keplerConfigs/config/environmentMapConfig/heatmapConf/rain";
import { getHeatmapWindSpeedNorm } from "keplerConfigs/config/environmentMapConfig/heatmapConf/wind";
import { getHeatmapPCountNorm } from "keplerConfigs/config/environmentMapConfig/heatmapConf/pcount";
import { getHeatmapConfigBarPressNorm } from "keplerConfigs/config/environmentMapConfig/heatmapConf/barPress";
import { getHeatmapConfigHumidityNorm } from "keplerConfigs/config/environmentMapConfig/heatmapConf/umidity";
import { getHeatmapConfigLuxNorm } from "keplerConfigs/config/environmentMapConfig/heatmapConf/lux";
import { getHeatmapConfigTempNorm } from "keplerConfigs/config/environmentMapConfig/heatmapConf/airTemp";
import ZoneSelectedSearchComponent from "components/cards/select/ZoneSelectedSearch.component";
import TitleSectionComponent from "components/cards/title/TitleSection.component";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import { getTimeZoneLondon } from "utils/timezone";
import ErrorCardcomponent from "components/common/card/ErrorCard.component";

const optionsChecked = {
  CO2: false,
  air_hum: false,
  air_temp: true,
  bar_press: false,
  lux: false,
  pcount: false,
  pm2_5: false,
  pm10: false,
  rain_intensity: false,
  wind_speed: false,
};

const AnimatedContainer = (props) => {
  const { t } = useTranslation();
  const options = useMemo(
    () => [
      {
        label: t("temperature"),
        name: "air_temp",
        value: "air_temp",
        data: [{ label: `${t("temperature")} `, endpoint: "air_temp" }],
        icon: (
          <ThermostatIcon style={{ width: 22, height: 22, color: "white" }} />
        ),
      },
      {
        label: t("illuminance"),
        name: "lux",
        value: "lux",
        icon: (
          <WbSunnyOutlinedIcon
            style={{ width: 22, height: 22, color: "white" }}
          />
        ),
        data: [{ label: `${t("illuminance")} `, endpoint: "lux" }],
      },
      {
        label: t("humidity"),
        name: "air_hum",
        value: "air_hum",
        icon: <IconDroplet style={{ width: 22, height: 22, color: "white" }} />,
        data: [{ label: `${t("humidity")} `, endpoint: "air_hum" }],
      },
      {
        label: "CO2",
        name: "CO2",
        value: "CO2",
        icon: <Co2Icon style={{ width: 24, height: 24, color: "white" }} />,
        data: [{ label: "CO2", endpoint: "CO2" }],
      },
      {
        label: t("atmospheric-pressure"),
        name: "bar_press",
        value: "bar_press",
        icon: <SpeedIcon style={{ width: 20, height: 20, color: "white" }} />,
        data: [
          { label: `${t("atmospheric-pressure")} `, endpoint: "bar_press" },
        ],
      },
      {
        label: t("Rainfall"),
        name: "rain_intensity",
        value: "rain_intensity",
        icon: (
          <IconCloudRain style={{ width: 20, height: 20, color: "white" }} />
        ),
        data: [
          { label: `${t("Intensity")} `, endpoint: "rain_intensity" },
          { label: `${t("Peak")} `, endpoint: "rain_intensity_peak" },
        ],
      },
      {
        label: "PM 10",
        name: "pm10",
        value: "pm10",
        icon: <WiDust style={{ width: 24, height: 24, color: "white" }} />,
      },
      {
        label: "PM 2.5",
        name: "pm2_5",
        value: "pm2_5",
        icon: <WiDust style={{ width: 24, height: 24, color: "white" }} />,
      },
      {
        label: t("Wind"),
        name: "wind_speed",
        value: "wind_speed",
        icon: <IconWind style={{ width: 20, height: 20, color: "white" }} />,
        data: [
          { label: `${t("Speed")} `, endpoint: "wind_speed" },
          { label: `${t("Direction")} `, endpoint: "wind_dir_idx" },
        ],
      },

      {
        label: t("people-counter"),
        name: "pcount",
        value: "pcount",
        icon: <IconUsers style={{ width: 20, height: 20, color: "white" }} />,
        data: [{ label: `${t("people-counter")} `, endpoint: "pcount" }],
      },
    ],
    [t]
  );

  const dispatch = useDispatch();
  const { token, cluster, user } = useSelector((state) => state.user);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingZone, setLoadingZone] = useState(true);
  const [dataHeatmap, setDataHeatmap] = useState({});
  const [zoneSelected, setZoneSelected] = useState(cluster ? cluster : []);
  const [listClusters, setListClusters] = useState([]);
  const [dateStart, setDateStart] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);
  const [valueData, setValueData] = useState("24h");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([options[0]]);
  const [checked, setChecked] = useState(optionsChecked);
  const [refresh, setRefresh] = useState(false);
  const [allStations, setAllStations] = useState(null);
  const [isFirstRefresh, setIsFirstRefresh] = useState(true);
  const [dataFilter, setDataFilter] = useState(null);
  const [valueSearch, setValueSearch] = useState(null);
  const [aggregatedData, setAggregatedData] = useState(null);

  const handleSearch = useCallback(
    (e) => {
      const searchValue = e.target.value;
      setValueSearch(searchValue);
      setDataHeatmap({});
      setConfig({});
      const searchResult = aggregatedData?.filter((item) => {
        return (
          item.stationId?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.tag?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.alias?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.address?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.cluster?.toLowerCase().includes(searchValue.toLowerCase())
        );
      });
      setDataFilter(searchResult);
    },
    [aggregatedData]
  );

  const onRefresh = useCallback(() => {
    setRefresh(!refresh);
  }, [refresh]);

  const onError = useCallback(
    (error) => {
      if (isFirstRefresh) {
        setLoading(false);
        if (error?.response?.status === 403) {
          onLogout(error);
        } else {
          if (error?.response?.status !== 401) {
            onFailure(error);
          } else {
            if (isFirstRefresh) {
              dispatch(
                refreshToken({
                  grant_type: "refresh_token",
                  refresh_token: user.refresh_token,
                })
              );
              setIsFirstRefresh(false);
            }
          }
        }
      } else {
        onLogout(error);
      }
    },
    // eslint-disable-next-line
    []
  );

  const onLogout = useCallback((error) => {
    if (error?.response?.status === 403 || error?.response?.status === 401) {
      dispatch(logout());
    }
    // eslint-disable-next-line
  }, []);

  const onFailure = useCallback((error) => {
    dispatch(
      openSnackbar({
        open: true,
        message: error.response?.data?.message
          ? error.response.data.message
          : "Errore",

        variant: "alert",
        alert: {
          color: "error",
        },
        close: false,
      })
    );
    // eslint-disable-next-line
  }, []);

  const onSuccess = useCallback((resp) => {
    setData(resp);
    setLoading(false);
  }, []);

  const onChangeDateStart = useCallback(
    (item) => {
      setDateStart(item);
      if (dateEnd === null && item === null) {
        setValueData("24h");
      } else {
        setValueData("");
      }
      setData(null);
      setDataFilter(null);
    },
    [dateEnd]
  );

  const onChangeValueDate = useCallback((item) => {
    setValueData(item);
    setDateStart(null);
    setDateEnd(null);
    setData(null);
  }, []);

  const onChangeDateEnd = useCallback(
    (item) => {
      setDateEnd(item);
      if (dateStart === null && item === null) {
        setValueData("24h");
      } else {
        setValueData("");
      }
      setData(null);
    },
    [dateStart]
  );

  const handleChangeZone = useCallback((item) => {
    setZoneSelected(item);
    dispatch(saveCluster(item));
    setData(null);
    setDataHeatmap({});
    setConfig({});
    // eslint-disable-next-line
  }, []);

  const onChange = useCallback(
    (item) => {
      if (!checked[item.name]) {
        setChecked({
          [item.name]: !checked[item.name],
        });
        setData(null);
      }
    },
    [checked]
  );

  const createInteractionConfig = useCallback(() => {
    const obj = {};
    const configKeys = [
      { key: "tdm_temp", name: "temperature" },
      { key: "CO2", name: "CO2" },
      { key: "lux", name: "illuminance" },
      { key: "humidity", name: "humidity" },
      { key: "bar_press", name: "atmospheric-pressure" },
      { key: "rain_intensity", name: "Rainfall intensity" },
      { key: "pm10", name: "PM10" },
      { key: "pm2_5", name: "PM2.5" },
      { key: "wind-speed", name: "wind-speed" },
      { key: "pcount", name: "People" },
    ];

    configKeys.map((item) => {
      obj[`${item.key}`] = [
        {
          name: "ID",
          format: null,
        },
        {
          name: "Cluster",
          format: null,
        },
        {
          name: t("Address"),
          format: null,
        },
        {
          name: t(item.name),
          format: null,
        },
        {
          name: t("Time"),
          format: null,
        },
      ];

      return obj;
    });

    let configInteraction = {
      tooltip: {
        fieldsToShow: obj,
        compareMode: false,
        compareType: "absolute",
        enabled: true,
      },
      brush: {
        size: 0.5,
        enabled: false,
      },
      geocoder: {
        enabled: false,
      },
      coordinate: {
        enabled: false,
      },
    };

    return configInteraction;
  }, [t]);

  const getFieldsTooltip = useCallback(
    (name) => {
      return [
        {
          name: "ID",
          type: "integer",
          format: "",
          analyzerType: "INT",
        },
        {
          name: "lat",
          type: "real",
          format: "",
          analyzerType: "FLOAT",
        },
        {
          name: "lon",
          type: "real",
          format: "",
          analyzerType: "FLOAT",
        },
        {
          name: "Cluster",
          format: null,
        },
        {
          name: t("Address"),
          format: null,
        },
        {
          name: name,
          type: "float",
          format: "",
          analyzerType: "FLOAT",
        },
        {
          name: "data_norm",
          type: "real",
          format: "",
          analyzerType: "real",
        },
        {
          name: t("Time"),
          type: "timestamp",
          format: "YYYY-M-DTH:m:s",
          analyzerType: "DATETIME",
        },
      ];
    },
    [t]
  );

  useEffect(() => {
    setLoadingZone(true);
    Request.stations
      .allPoi({
        token: token,
      })
      .then((resp) => {
        setAllStations(resp);
        setLoadingZone(false);
        setIsFirstRefresh(true);
      })
      .catch((error) => onError(error));
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    //Ottengo dalla lista delle stazioni i cluster esistenti
    if (allStations && !_.isEmpty(allStations.stations)) {
      const clusters = allStations.stations.filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.cluster === value.cluster)
      );
      const list = clusters.map((item) => {
        return item["cluster"];
      });

      setListClusters(list.sort());
    }
  }, [allStations, refresh]);

  useEffect(() => {
    const metricSelected = options.filter(
      (item) => checked[item.name] === true
    );
    if (metricSelected !== selected) {
      setSelected(metricSelected);
    }

    // eslint-disable-next-line
  }, [checked, refresh]);

  useEffect(() => {
    if (dataFilter && !_.isEmpty(dataFilter)) {
      if (!_.isEmpty(selected)) {
        switch (selected[0].value) {
          case "air_temp":
            const fieldsTemp = getFieldsTooltip(t("temperature"));
            setDataHeatmap(
              getTempHeatmap(dataFilter, t("temperature"), fieldsTemp)
            );
            break;
          case "air_hum":
            const fieldsHum = getFieldsTooltip(t("humidity"));
            setDataHeatmap(
              getUmidityHeatmap(dataFilter, t("humidity"), fieldsHum)
            );
            break;
          case "lux":
            const fieldsLux = getFieldsTooltip(t("illuminance"));
            setDataHeatmap(
              getLuxHeatmap(dataFilter, t("illuminance"), fieldsLux)
            );
            break;
          case "CO2":
            const fieldsCO2 = getFieldsTooltip("CO2");
            setDataHeatmap(getCo2Heatmap(dataFilter, fieldsCO2));
            break;
          case "bar_press":
            const fieldsPress = getFieldsTooltip(t("atmospheric-pressure"));
            setDataHeatmap(
              getBarPressHeatmap(
                dataFilter,
                t("atmospheric-pressure"),
                fieldsPress
              )
            );
            break;
          case "rain_intensity":
            const fieldsRain = getFieldsTooltip(t("Rainfall intensity"));
            setDataHeatmap(
              getRainIntensityHeatmap(
                dataFilter,
                t("Rainfall intensity"),
                fieldsRain
              )
            );
            break;
          case "pm10":
            const fieldsPM10 = getFieldsTooltip("PM10");
            setDataHeatmap(getPM10Heatmap(dataFilter, fieldsPM10));
            break;
          case "pm2_5":
            const fieldsPM2_5 = getFieldsTooltip("PM2.5");
            setDataHeatmap(getPM2_5Heatmap(dataFilter, fieldsPM2_5));
            break;
          case "wind_speed":
            const fieldsWind = getFieldsTooltip(t("wind-speed"));
            setDataHeatmap(
              getWindSpeedAvgHeatmap(dataFilter, t("wind-speed"), fieldsWind)
            );
            break;
          case "pcount":
            const fieldsPCount = getFieldsTooltip(t('People'));

            setDataHeatmap(
              getPCountHeatmap(dataFilter, t("People"), fieldsPCount)
            );
            break;
          default:
            return;
        }
        setLoading(false);
      }
    } else {
      setDataHeatmap({});
      setConfig({});
    }
  }, [dataFilter, selected, refresh, valueData, t, getFieldsTooltip]);

  useEffect(() => {
    if (!_.isEmpty(selected)) {
      setDataFilter(null);
      setConfig({});
      if (
        dateStart &&
        DateTime.fromISO(dateStart).isValid &&
        dateEnd &&
        DateTime.fromISO(dateEnd).isValid
      ) {
        const start = getTimeZoneLondon(
          new DateTime(dateStart).toFormat("yyyy-MM-dd'T'HH:mm:ss")
        );
        const end = getTimeZoneLondon(
          new DateTime(dateEnd).toFormat("yyyy-MM-dd'T'HH:mm:ss")
        );

        const path =
          zoneSelected && !_.isEmpty(zoneSelected)
            ? `?cluster=${zoneSelected}&datetime_start=${start}&datetime_end=${end}`
            : `?datetime_start=${start}&datetime_end=${end}`;
        setLoading(true);

        Request.stations
          .getMetricByStationGeneric({
            metric: selected[0].value,
            token: token,
            path: path,
          })
          .then((resp) => {
            onSuccess(resp.entries);
          })
          .catch((error) => {
            setData(null);
            onError(error);
          });
      } else {
        if (dateStart && DateTime.fromISO(dateStart).isValid) {
          const start = getTimeZoneLondon(
            new DateTime(dateStart).toFormat("yyyy-MM-dd'T'HH:mm:ss")
          );

          const path =
            zoneSelected && !_.isEmpty(zoneSelected)
              ? `?cluster=${zoneSelected}&datetime_start=${start}`
              : `?datetime_start=${start}`;
          setLoading(true);

          Request.stations
            .getMetricByStationGeneric({
              metric: selected[0].value,
              token: token,
              path: path,
            })
            .then((resp) => {
              onSuccess(resp.entries);
            })
            .catch((error) => {
              setData(null);
              onError(error);
            });
        } else {
          if (dateEnd && DateTime.fromISO(dateEnd).isValid) {
            const end = getTimeZoneLondon(
              new DateTime(dateEnd).toFormat("yyyy-MM-dd'T'HH:mm:ss")
            );

            setLoading(true);
            const path =
              zoneSelected && !_.isEmpty(zoneSelected)
                ? `?cluster=${zoneSelected}&datetime_end=${end}`
                : `?datetime_end=${end}`;

            Request.stations
              .getMetricByStationGeneric({
                metric: selected[0].value,
                token: token,
                path: path,
              })
              .then((resp) => {
                onSuccess(resp.entries);
              })
              .catch((error) => {
                setData(null);
                onError(error);
              });
          } else {
            setLoading(true);
            const path =
              zoneSelected && !_.isEmpty(zoneSelected)
                ? `?cluster=${zoneSelected}`
                : "";
            Request.stations
              .getMetricByStationGeneric({
                metric: selected[0].value,
                token: token,
                path: path,
              })
              .then((resp) => {
                onSuccess(resp.entries);
              })
              .catch((error) => {
                setData(null);
                onError(error);
              });
          }
        }
      }
    }
    // eslint-disable-next-line
  }, [selected, zoneSelected, dateStart, dateEnd, refresh, token]);

  useEffect(() => {
    if (_.isEmpty(dataFilter)) {
      setConfig({
        datasets: [],
        options: {
          centerMap: true,
          readOnly: true,
        },
        config: {
          visState: {
            interactionConfig: [],

            layers: [],

            layerBlending: "normal",
            splitMaps: [],
            animationConfig: {
              currentTime: null,
              speed: 1,
            },
          },
          mapState: mapStateConfig,
          mapStyle: mapStyleConfig,
        },
      });
      //setLoading(false)
    } else {
      if (dataHeatmap && dataHeatmap?.data?.rows && dataHeatmap?.data?.id) {
        const dataMap = dataHeatmap;
        const configTest = {
          datasets: dataMap,
          options: {
            centerMap: true,
            readOnly: true,
          },
          config: {
            visState: {
              interactionConfig: createInteractionConfig(),

              layers: [
                getPointCO2Heatmap(0),
                getHeatmapCO2Norm(10),

                getPointConfigUmidityHeatmap(t("humidity"), 0),
                getHeatmapConfigHumidityNorm(t("humidity"), 10),

                getPointConfigTempHeatmap(t("temperature"), 0),
                getHeatmapConfigTempNorm(t("temperature"), 10),

                getPointConfigLuxHeatmap(t("illuminance"), 0),
                getHeatmapConfigLuxNorm(10),

                getPointConfigBarPressHeatmap(t("atmospheric-pressure"), 0),
                getHeatmapConfigBarPressNorm(t("atmospheric-pressure"), 10),

                getHeatmapPM10Norm(10),
                getPointPM10Heatmap(0),

                getPointPM2_5Heatmap(0),
                getHeatmapPM2_5Norm(10),

                getPointConfigRainfallHeatmap(t("rainfall-intensity"), 0),
                getHeatmapRainfallNorm(t("Rainfall intensity"), 10),

                getPointConfigWindSpeedHeatmap(t("wind-speed"), 0),
                getHeatmapWindSpeedNorm(t("wind-speed"), 10),

                getPointConfigPCountHeatmap(t("people-counter"), 0),
                getHeatmapPCountNorm(t("People"), 10),
              ],

              layerBlending: "normal",
              splitMaps: [],
              animationConfig: {
                currentTime: null,
                speed: 1,
              },
            },
            mapState: mapStateConfig,
            mapStyle: mapStyleConfig,
          },
        };
        setConfig(configTest);
      }
    }
    // eslint-disable-next-line
  }, [dataHeatmap, refresh, t, dataFilter]);

  useEffect(() => {
    if (valueSearch) {
      handleSearch({ target: { value: valueSearch } });
    }
  }, [valueSearch, handleSearch]);

  useEffect(() => {
    if (allStations?.stations && data) {
      let union = [];

      data?.map((station) => {
        const obj = _.find(
          allStations?.stations,
          (item) => item?.stationId === station?.station
        );
        union.push({
          ...obj,
          ...station,
        });

        return obj;
      });
      setAggregatedData(union);
      setDataFilter(union);
    }
  }, [allStations, data]);

  return (
    <Fragment>
      {loadingZone ? (
        <Loading />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TitleSectionComponent title={t("Animated historical map")} />
          </Grid>

          <Grid item xs={12}>
            <ZoneSelectedSearchComponent
              listClusters={listClusters}
              zoneSelected={zoneSelected}
              loadingZone={loadingZone}
              onRefresh={onRefresh}
              handleChangeZone={handleChangeZone}
              handleSearch={handleSearch}
              dateStart={dateStart}
              dateEnd={dateEnd}
              valueData={valueData}
              onChangeDateStart={onChangeDateStart}
              onChangeDateEnd={onChangeDateEnd}
              onChangeValueDate={onChangeValueDate}
              hasTimeSelect={true}
              valueSearch={valueSearch}
            />
          </Grid>

          {loading ? (
            <Grid item xs={12}>
              <Loading />
            </Grid>
          ) : (
            <Fragment>
              {_.isEmpty(dataFilter) && (
                <Grid item xs={12}>
                  <ErrorCardcomponent
                    message={t("No data for selected filters")}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Box sx={{ position: "relative" }}>
                  <FiltersKeplerMapContainer
                    height={800}
                    config={config}
                    isLoading={loading}
                    selected={selected}
                    options={options}
                    checked={checked}
                    onChange={onChange}
                    {...props}
                  />
                </Box>
              </Grid>
            </Fragment>
          )}
        </Grid>
      )}
    </Fragment>
  );
};

export default AnimatedContainer;
